import { setupChameleon } from "PFApp/bootstrapper/setup_chameleon";
import { setupWrapAjax } from "PFApp/bootstrapper/setup_wrap_ajax";
import { useChameleonProfilePayload } from "PFApp/bootstrapper/use_chameleon_profile_payload";
import { useCustomConfig } from "PFApp/bootstrapper/use_custom_config";
import { dataDogSetUser, useDataDogRum } from "PFApp/bootstrapper/use_datadog_rum";
import { useInjectTheme } from "PFApp/bootstrapper/use_inject_theme";
import { useLogInOutBroadcasts } from "PFApp/bootstrapper/use_log_in_out_broadcasts";
import { useNativeNotifications } from "PFApp/bootstrapper/use_native_notifications";
import { useNotificationSettings } from "PFApp/bootstrapper/use_notification_settings";
import { useStorage } from "PFApp/bootstrapper/use_storage";
import { useTemplatesConfiguration } from "PFApp/bootstrapper/use_templates_configuration";
import { useGlobalsForLocale } from "PFApp/use_globals_for_locale";
import { useGrowl } from "PFApp/use_growl";
import { useSession } from "PFApp/use_session";
import { useThemeVars } from "PFApp/use_theme_vars";
import { setupAxiosInstance } from "PFCore/api";
import { usePusher } from "PFCore/base/pusher";
import { initializeMsTeams } from "PFCore/helpers/ms_teams";
import { useQueryParams } from "PFCore/helpers/use_query_params";
import { removeLoadingState } from "PFCore/initializers/remove_loading_state";
import { setStorage } from "PFCore/initializers/set_storage";
import fetchCurrentAccount from "PFCore/services/accounts/current_account";
import { fetchCurrentProfile } from "PFCore/services/current_profile";
import { CURRENT_ACCOUNT_ACTIONS } from "PFReducers/current_account_reducer";
import { CURRENT_PROFILE_ACTIONS } from "PFReducers/current_profile_reducer";
import { useContext, useEffect, useState } from "react";

import AppContext from "./app_context";
import { useChangeLanguage } from "./use_change_language";

export const useBootstrapper = ({ dispatch, store }) => {
  const [initialized, setInitialized] = useState(false);

  setStorage();
  useStorage();

  useCustomConfig();
  useGlobalsForLocale(store);
  useDataDogRum();
  useLogInOutBroadcasts();
  useThemeVars(dispatch);
  useNotificationSettings(dispatch, store);
  useNativeNotifications(dispatch, store);
  const { isReady: isTemplateConfigurationReady } = useTemplatesConfiguration(dispatch, store);

  useChameleonProfilePayload();

  const queryParams = useQueryParams();
  const session = useSession();
  const growl = useGrowl();
  const {
    store: {
      notifications: { showUnreadOnly }
    }
  } = useContext(AppContext);

  const fetchCurrentAccountData = async () => {
    try {
      const account = await fetchCurrentAccount();
      if (account) {
        dispatch({
          type: CURRENT_ACCOUNT_ACTIONS.CURRENT_ACCOUNT_READY,
          payload: account
        });
      }
    } catch (error) {
      if (error.status === 404) {
        window.location.replace("https://profinda.com");
      }
    }
  };

  // fetch current account
  useEffect(() => {
    const fetchCurrentAccountAndInitialize = async () => {
      const forceInitializeMsTeam =
        queryParams.get("msTeams") && queryParams.get("oauthRedirectMethod") !== "deeplink";
      await initializeMsTeams({ force: forceInitializeMsTeam, account: queryParams.get("account") });
      fetchCurrentAccountData();
    };
    fetchCurrentAccountAndInitialize();
  }, []);

  // fetch current profile and set up the rest
  useEffect(() => {
    const fetchCurrentProfileAndSetUp = async () => {
      const { currentAccount } = store;
      let { currentProfile } = store;

      if (currentAccount?.id) {
        setupChameleon(currentAccount);
        const theme = PF.config.theme || currentAccount.customization?.theme || "profinda";
        const { themeConfig } = PF.config;
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useInjectTheme(theme, { themeConfig });
        const token = await session.setup();
        setupWrapAjax($, session, currentAccount);
        setupAxiosInstance(session, currentAccount.full_domain);
        try {
          if (token || session.getAccessToken()) {
            currentProfile = await fetchCurrentProfile(dispatch);
            dispatch({
              type: CURRENT_PROFILE_ACTIONS.CURRENT_PROFILE_SET,
              payload: { ...currentProfile, loaded: true, logged_in: true } // eslint-disable-line camelcase
            });
            await fetchCurrentAccountData();
          } else {
            throw "not signed in";
          }
        } catch {
          dispatch({
            type: CURRENT_PROFILE_ACTIONS.CURRENT_PROFILE_SET,
            payload: { loaded: true, logged_in: false } // eslint-disable-line camelcase
          });
        }

        removeLoadingState();
        setInitialized(true);

        if (PF.config.environment === "staging" && PF.config.ui_branch !== "staging") {
          growl({
            message: `Be aware: You are using git branch ${PF.config.ui_branch}`,
            kind: "alert"
          });
        }

        const isAccountSwitched =
          currentProfile.account_id && currentProfile.account_id !== currentAccount.id;

        if (isAccountSwitched) {
          growl({
            message: `You switched account, be careful`,
            kind: "alert"
          });
        }

        onComplete();
      }
    };
    fetchCurrentProfileAndSetUp();
  }, [store.currentAccount?.id]);

  useEffect(() => {
    const { currentProfile, currentAccount } = store;

    if (currentProfile.loaded && currentProfile.logged_in) {
      dataDogSetUser(currentProfile, currentAccount);
      session.startHeartbeat();
    }
  }, [store.currentProfile.id, store.currentAccount.id, showUnreadOnly]);

  useChangeLanguage();
  usePusher();

  return { initialized: initialized && isTemplateConfigurationReady };
};

const onComplete = () => {
  if (
    PF.config.environment !== "development" &&
    PF.config.force_ssl !== "false" &&
    location.protocol !== "https:"
  ) {
    window.location = document.URL.replace(/^http:/, "https:");
  }
};
